/**
 * DEPRECATED — `registerForEvents` CustomEvent bridge.
 *
 * Isolated here on purpose. `registerForEvents` is deprecated sugar over a
 * one-line listener (`addEventListener(name, e => loader.sendMetadata(e.detail))`)
 * kept only to ease Pype 1.0 migration. This module exists so the entire
 * deprecated surface can be removed in one mechanical step.
 *
 * ── Removal recipe (when we drop `registerForEvents`) ────────────────────
 *   1. Delete this file.
 *   2. In `loader.ts`: delete the `_eventBridge` field, the `EventListenerBridge`
 *      import, the `registerForEvents` public method, and the
 *      `_eventBridge?.teardown()` + `_eventBridge = null` lines in
 *      `_unmountElement`.
 *   3. In `types.ts`: remove `'registerForEvents'` from `LoaderCommand` and from
 *      every `ALLOWED_COMMANDS` entry in `loader.ts`.
 * Nothing in `sendMetadata`, the metadata buffer, validation, or transport
 * references the bridge — the only coupling is the `(detail) => void` sink
 * passed to the constructor, which is a plain closure. Removal touches
 * nothing in the core path.
 * ─────────────────────────────────────────────────────────────────────────
 */

interface TrackedListener {
  target: EventTarget;
  name: string;
  handler: EventListener;
}

/**
 * Bridges parent-page CustomEvents into a metadata sink. Owns its own
 * listener tracking + teardown; depends only on the sink closure, never on
 * the Loader instance or any widget internals.
 */
export class EventListenerBridge {
  private readonly _sink: (detail: unknown) => void;

  private _listeners: TrackedListener[] = [];

  /**
   * @param sink - Called with each captured event's `detail`. In the Loader
   *   this is `detail => loader._enqueueMetadata(detail, { onInvalid: 'warn' })`
   *   — a closure, so the bridge never holds an `loader` reference.
   */
  constructor(sink: (detail: unknown) => void) {
    this._sink = sink;
  }

  /**
   * Subscribe to `eventNames` on `eventTarget` (default `window`). Each
   * fired event forwards its `detail` to the sink. Idempotent on
   * `(target, name)` — registering the same pair twice attaches one
   * listener, so a single dispatch forwards once.
   */
  register(eventNames: string[], eventTarget: EventTarget = window): void {
    for (const name of eventNames) {
      const already = this._listeners.some(
        (l) => l.target === eventTarget && l.name === name
      );
      if (already) continue;
      const handler: EventListener = (event: Event) => {
        if (!(event instanceof CustomEvent)) return;
        this._sink((event as CustomEvent).detail);
      };
      eventTarget.addEventListener(name, handler);
      this._listeners.push({ target: eventTarget, name, handler });
    }
  }

  /**
   * Remove every tracked listener and empty the tracking array. Emptying
   * (not just `removeEventListener`) is required so a subsequent
   * register on the same long-lived target can't double-attach.
   */
  teardown(): void {
    for (const { target, name, handler } of this._listeners) {
      target.removeEventListener(name, handler);
    }
    this._listeners = [];
  }
}
